import React, { useEffect } from 'react';
import { useUI } from '../hooks';
import { ToastType } from '../types';
import './Toast.css';

interface ToastProps {
  type: ToastType;
  message: string;
  visible: boolean;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ type, message, visible, onClose }) => {
  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (visible) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [visible, onClose]);
  
  // Don't render if not visible
  if (!visible) {
    return null;
  }
  
  // Determine icon based on type
  let icon;
  if (type === 'success') {
    icon = (
      <svg viewBox="0 0 24 24" width="24" height="24" stroke="#2e8b57" fill="none">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M22 4L12 14.01l-3-3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  } else if (type === 'error') {
    icon = (
      <svg viewBox="0 0 24 24" width="24" height="24" stroke="#dc3545" fill="none">
        <circle cx="12" cy="12" r="10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="15" y1="9" x2="9" y2="15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="9" y1="9" x2="15" y2="15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  } else if (type === 'warning') {
    icon = (
      <svg viewBox="0 0 24 24" width="24" height="24" stroke="#ffc107" fill="none">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="12" y1="9" x2="12" y2="13" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="12" y1="17" x2="12.01" y2="17" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  } else {
    icon = (
      <svg viewBox="0 0 24 24" width="24" height="24" stroke="#17a2b8" fill="none">
        <circle cx="12" cy="12" r="10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="12" y1="16" x2="12" y2="12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="12" y1="8" x2="12.01" y2="8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  
  return (
    <div className={`toast toast-${type}`}>
      <div className="toast-icon">
        {icon}
      </div>
      <div className="toast-content">
        {message}
      </div>
      <button className="toast-close" onClick={onClose}>
        <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none">
          <line x1="18" y1="6" x2="6" y2="18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="6" y1="6" x2="18" y2="18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
};

// Toast container component
export const ToastContainer: React.FC = () => {
  const { toastState, hideToast } = useUI();
  
  return (
    <div className="toast-container">
      <Toast
        type={toastState.type}
        message={toastState.message}
        visible={toastState.visible}
        onClose={hideToast}
      />
    </div>
  );
};

export default Toast;